import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ClipboardList, Search, Loader, RefreshCw, Calendar, Clock,
  BookOpen, FileText, Eye, Upload, CheckCircle, X, PenLine,
  BarChart2, Filter, Maximize2, Minimize2, Download, Play, Award,
  Globe, Layers, Sparkles, Check, Target, Trophy, Tag,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import html2pdf from "html2pdf.js";
import {
  lmsGetExams, lmsGetQuestionPapers, lmsGetManualPapers,
  lmsGetAnswerSheets, lmsUploadAnswerSheet, lmsGetCertificates,
  lmsIssueCertificate,
} from "../api/lmsApi";
import { GetPaperMetaDataThunk } from "../../QuestionPaper/createpaper/redux/thunk";
import { getContent } from "../../QuestionPaper/createpaper/redux/selectors";
import { selectLoader } from "../../QuestionPaper/createpaper/components/others";
import "../lms.css";
import "../../QuestionPaper/css/questionPaper.css";

const getStudentData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); }
  catch { return {}; }
};

const getExamStatus = (exam) => {
  if (!exam.exam_date || !exam.start_time) return "open";
  // Use local date to avoid UTC midnight shifting the date by one day in IST/other timezones
  const d = new Date(exam.exam_date);
  const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const examStart = new Date(`${dateStr}T${exam.start_time}`);
  const now       = new Date();
  if (now < examStart) return "not_started";
  if (exam.duration_minutes) {
    const examEnd = new Date(examStart.getTime() + exam.duration_minutes * 60 * 1000);
    if (now > examEnd) return "ended";
  }
  return "open";
};

const formatTimeStr = (t) => {
  if (!t) return "";
  try {
    const parts = t.split(":");
    let h = parseInt(parts[0], 10);
    const m = parts[1] || "00";
    if (isNaN(h)) return t;
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  } catch {
    return t;
  }
};

const getExamTimings = (exam) => {
  if (!exam) return { startDateStr: "Date TBD", startTimeFormatted: "", endDateStr: "Date TBD", endTimeFormatted: "" };

  const startDateStr = exam.exam_date
    ? new Date(exam.exam_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "Date TBD";

  const startTimeFormatted = formatTimeStr(exam.start_time);

  let endDateStr = exam.end_date
    ? new Date(exam.end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : startDateStr;

  let endTimeFormatted = exam.end_time ? formatTimeStr(exam.end_time) : "";

  if (!endTimeFormatted && exam.exam_date && exam.start_time) {
    try {
      const d = new Date(exam.exam_date);
      const dateOnly = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const examStart = new Date(`${dateOnly}T${exam.start_time}`);
      if (!isNaN(examStart.getTime())) {
        const durationMin = Number(exam.duration_minutes) || 50;
        const examEnd = new Date(examStart.getTime() + durationMin * 60 * 1000);
        endDateStr = examEnd.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        let h = examEnd.getHours();
        const m = String(examEnd.getMinutes()).padStart(2, "0");
        const ampm = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12;
        endTimeFormatted = `${h}:${m} ${ampm}`;
      }
    } catch {
      // Fallback silently
    }
  }

  return { startDateStr, startTimeFormatted, endDateStr, endTimeFormatted };
};

const TAG_STYLES = {
  mock:        { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe", label: "Mock" },
  unit_test:   { bg: "#faf5ff", color: "#7c3aed", border: "#e9d5ff", label: "Unit Test" },
  half_yearly: { bg: "#fffbeb", color: "#d97706", border: "#fde68a", label: "Half Yearly" },
  annual:      { bg: "#fef2f2", color: "#ef4444", border: "#fecaca", label: "Annual" },
  practice:    { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0", label: "Practice" },
};

const EXAM_TYPE_COLORS = {
  unit_test:   { bg: "#ede9fe", color: "#6d28d9", grad: "linear-gradient(135deg,#6d28d9,#7c3aed)" },
  half_yearly: { bg: "#fef9c3", color: "#b45309", grad: "linear-gradient(135deg,#b45309,#d97706)" },
  annual:      { bg: "#fee2e2", color: "#b91c1c", grad: "linear-gradient(135deg,#b91c1c,#dc2626)" },
  mock:        { bg: "#dbeafe", color: "#1d4ed8", grad: "linear-gradient(135deg,#1d4ed8,#3b82f6)" },
  practice:    { bg: "#dcfce7", color: "#166534", grad: "linear-gradient(135deg,#166534,#16a34a)" },
};
const EXAM_TYPE_LABELS = {
  unit_test: "Unit Test", half_yearly: "Half Yearly", annual: "Annual",
  mock: "Mock", practice: "Practice",
};

/* ── Answer Upload Section ── */
function AnswerUploadPanel({ exam, schoolId, studentId }) {
  const [answerSheet, setAnswerSheet] = useState(null);
  const [file,        setFile]        = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [loadingAns,  setLoadingAns]  = useState(true);
  const fileRef = React.useRef();

  useEffect(() => {
    if (!studentId || !exam?.id) { setLoadingAns(false); return; }
    lmsGetAnswerSheets({ exam_id: exam.id, student_id: studentId })
      .then((res) => setAnswerSheet((res?.data?.data || [])[0] || null))
      .catch(() => {})
      .finally(() => setLoadingAns(false));
  }, [exam?.id, studentId]);

  const handleUpload = async () => {
    if (!file) return toast.error("Please select a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file",       file);
      fd.append("school_id",  schoolId);
      fd.append("exam_id",    exam.id);
      fd.append("student_id", studentId);
      await lmsUploadAnswerSheet(fd);
      toast.success("Answer sheet submitted!");
      setFile(null);
      const res = await lmsGetAnswerSheets({ exam_id: exam.id, student_id: studentId });
      setAnswerSheet((res?.data?.data || [])[0] || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally { setUploading(false); }
  };

  if (loadingAns) return null;

  return (
    <div style={{ marginTop: 16, border: "1.5px solid #e0e7ff", borderRadius: 10, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", background: "linear-gradient(90deg,#4f46e5,#7c3aed)",
        display: "flex", alignItems: "center", gap: 7 }}>
        <Upload size={14} color="#fff" />
        <span style={{ fontWeight: 700, fontSize: 13, color: "#fff" }}>Your Answer Sheet</span>
      </div>
      <div style={{ padding: "14px" }}>
        {answerSheet ? (
          <div style={{ padding: "10px 12px", background: "#f0fdf4",
            border: "1.5px solid #86efac", borderRadius: 8, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CheckCircle size={16} color="#16a34a" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d" }}>Submitted</div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>{answerSheet.file_name}</div>
              </div>
              <a href={answerSheet.file_url} target="_blank" rel="noreferrer" style={{
                fontSize: 12, fontWeight: 700, color: "#4f46e5", textDecoration: "none",
              }}>View</a>
            </div>
            {answerSheet.marks_obtained != null && (
              <div style={{ marginTop: 8, padding: "6px 10px", background: "#fff",
                border: "1px solid #86efac", borderRadius: 6, fontSize: 13, fontWeight: 700, color: "#15803d" }}>
                Score: {answerSheet.marks_obtained} / {exam.total_marks || "—"}
                {answerSheet.grade && ` · Grade: ${answerSheet.grade}`}
                {answerSheet.teacher_remarks && (
                  <div style={{ fontSize: 11, fontWeight: 400, color: "#6b7280", marginTop: 4 }}>
                    Remarks: {answerSheet.teacher_remarks}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 10px" }}>
            Download the question paper, write your answers, then upload here as PDF or photo.
          </p>
        )}

        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }} onChange={(e) => setFile(e.target.files[0] || null)} />

        {file && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
            background: "#f3f4f6", borderRadius: 7, marginBottom: 8, fontSize: 12 }}>
            <FileText size={13} color="#4f46e5" />
            <span style={{ flex: 1, color: "#374151" }}>{file.name}</span>
            <button onClick={() => setFile(null)} style={{ border: "none", background: "transparent",
              cursor: "pointer", color: "#9ca3af", fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => fileRef.current?.click()} style={{
            flex: 1, padding: "8px 0", borderRadius: 7, border: "2px dashed #a5b4fc",
            background: "#f5f3ff", color: "#4f46e5", fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}>&#128206; {file ? "Change File" : "Choose File"}</button>
          {file && (
            <button onClick={handleUpload} disabled={uploading} style={{
              flex: 1, padding: "8px 0", borderRadius: 7, border: "none",
              background: uploading ? "#a5b4fc" : "linear-gradient(135deg,#4f46e5,#7c3aed)",
              color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              {uploading ? <Loader size={12} className="ms2-spin" /> : <Upload size={12} />}
              {uploading ? "Submitting..." : "Submit"}
            </button>
          )}
        </div>
        {answerSheet && (
          <p style={{ fontSize: 11, color: "#9ca3af", margin: "8px 0 0", textAlign: "center" }}>
            Re-upload to replace your previous submission
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Student Question Paper Utility Viewer ── */
function StudentUtilityPaperViewer({ questionPaperId, exam, isExpanded }) {
  const dispatch = useDispatch();
  const { loader } = useSelector((store) => store.createQstnPaper || {});
  const content = useSelector(getContent);
  const [downloading, setDownloading] = useState(false);
  const paperRef = useRef(null);

  useEffect(() => {
    if (questionPaperId) {
      dispatch(GetPaperMetaDataThunk({ paperId: questionPaperId, type: 1 }));
    }
  }, [dispatch, questionPaperId]);

  const handleDownloadPdf = () => {
    if (!paperRef.current) return;
    setDownloading(true);
    try {
      const element = paperRef.current;
      const cleanExamName = (exam?.name || "Exam").replace(/[^a-zA-Z0-9_-]/g, "_");
      const opt = {
        margin: [0.3, 0.3, 0.3, 0.3],
        filename: `${cleanExamName}_Question_Paper.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      html2pdf()
        .set(opt)
        .from(element)
        .save()
        .then(() => {
          toast.success("Question paper downloaded as PDF!");
          setDownloading(false);
        })
        .catch((err) => {
          console.error("PDF download error:", err);
          toast.error("Failed to generate PDF");
          setDownloading(false);
        });
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error("Failed to generate PDF");
      setDownloading(false);
    }
  };

  return (
    <div style={{
      border: "1.5px solid #e0e7ff", borderRadius: 10, overflow: "hidden",
      background: "#fff", marginBottom: 18,
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        padding: "10px 14px", background: "#ede9fe",
        borderBottom: "1px solid #ddd6fe", display: "flex", alignItems: "center",
        justifyContent: "space-between", flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 13, color: "#6d28d9" }}>
          <FileText size={15} color="#7c3aed" /> Question Paper
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "#7c3aed", fontWeight: 600 }}>
            Paper ID: {questionPaperId}
          </span>
          {content && !loader && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloading}
              title="Download question paper as PDF"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 6,
                background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                color: "#fff", border: "none", cursor: downloading ? "not-allowed" : "pointer",
                boxShadow: "0 2px 6px rgba(79,70,229,0.25)",
              }}
            >
              {downloading ? <Loader size={11} className="ms2-spin" /> : <Download size={12} />}
              {downloading ? "Downloading..." : "Download PDF"}
            </button>
          )}
        </div>
      </div>
      <div style={{
        padding: "16px 14px",
        maxHeight: isExpanded ? "65vh" : 420,
        overflowY: "auto", overflowX: "auto",
        background: "#fafafa",
        scrollbarWidth: "thin",
        scrollbarColor: "#c7d2fe #f1f5f9",
      }}>
        {loader ? (
          <div style={{ textAlign: "center", padding: 36 }}>
            <Loader size={26} className="ms2-spin" color="#7c3aed" />
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>Loading question paper...</p>
          </div>
        ) : content ? (
          <div
            ref={paperRef}
            className={`content-stage-3 ${selectLoader ? selectLoader(loader) : ""}`}
            dangerouslySetInnerHTML={{ __html: content }}
            style={{ width: "100%", maxWidth: "100%", margin: 0, boxSizing: "border-box", background: "#fff", padding: "12px" }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: 36, color: "#9ca3af", fontSize: 13 }}>
            No question paper content available.
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Exam Detail Panel ── */
function ExamDetailPanel({ exam, schoolId, studentId, nav, onClose, cert, onOpenCert }) {
  const tc = EXAM_TYPE_COLORS[exam.exam_type] || EXAM_TYPE_COLORS.unit_test;
  const [pdfPapers,    setPdfPapers]    = useState([]);
  const [manualPapers, setManualPapers] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [isExpanded,   setIsExpanded]   = useState(false);

  const hasUtilityPaper = Boolean(exam?.question_paper_id);
  const isOnline = exam.mode === "online";

  useEffect(() => {
    setLoading(true);
    Promise.all([
      lmsGetQuestionPapers({ exam_id: exam.id }),
      lmsGetManualPapers({ exam_id: exam.id }),
    ]).then(([pdfRes, manRes]) => {
      setPdfPapers(pdfRes?.data?.data || []);
      setManualPapers(manRes?.data?.data || []);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, [exam.id]);

  const { startDateStr, startTimeFormatted, endDateStr, endTimeFormatted } = getExamTimings(exam);

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200,
        backdropFilter: "blur(2px)",
      }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: isExpanded ? "85vw" : 480,
        maxWidth: "96vw",
        background: "#fff", zIndex: 201, display: "flex", flexDirection: "column",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.18)",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}>
        {/* Gradient top bar */}
        <div style={{ height: 4, background: tc.grad, flexShrink: 0 }} />

        {/* Header */}
        <div style={{
          padding: "18px 20px", borderBottom: "1px solid #e5e7eb",
          background: `linear-gradient(135deg,${tc.color}0d,${tc.color}05)`,
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div style={{ flex: 1, paddingRight: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 20,
                  background: isOnline ? "#dbeafe" : "#ede9fe", color: isOnline ? "#1e40af" : "#5b21b6",
                  display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {isOnline ? <Globe size={11} /> : <FileText size={11} />}
                  {isOnline ? "Online Exam" : "Offline Exam"}
                </span>
                <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 20,
                  background: tc.bg, color: tc.color }}>
                  {EXAM_TYPE_LABELS[exam.exam_type] || exam.exam_type}
                </span>
              </div>
              <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", lineHeight: 1.3 }}>
                {exam.name}
              </div>
              {exam.class_name && (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>
                  {exam.class_name}{exam.subject_name ? ` · ${exam.subject_name}` : ""}
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                title={isExpanded ? "Collapse" : "Expand"}
                style={{
                  border: "none", background: "#f3f4f6",
                  cursor: "pointer", color: "#6b7280", borderRadius: 8, padding: 6, lineHeight: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
              <button onClick={onClose} style={{ border: "none", background: "#f3f4f6",
                cursor: "pointer", color: "#6b7280", borderRadius: 8, padding: 6, lineHeight: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <X size={18} />
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            {startDateStr !== "Date TBD" && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12,
                color: "#374151", background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 7, padding: "4px 10px" }}>
                <Calendar size={12} color="#9ca3af" /> Start: {startDateStr}
                {startTimeFormatted && ` · ${startTimeFormatted}`}
              </div>
            )}
            {endTimeFormatted && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12,
                color: "#374151", background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 7, padding: "4px 10px" }}>
                <Calendar size={12} color="#9ca3af" /> End: {endDateStr} · {endTimeFormatted}
              </div>
            )}
            {exam.duration_minutes && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12,
                color: "#374151", background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 7, padding: "4px 10px" }}>
                <Clock size={12} color="#9ca3af" /> {exam.duration_minutes} min
              </div>
            )}
            {exam.total_marks && (
              <div style={{ fontSize: 12, color: tc.color, fontWeight: 700,
                background: tc.bg, borderRadius: 7, padding: "4px 10px" }}>
                {exam.total_marks} marks
                {exam.passing_marks ? ` · Pass: ${exam.passing_marks}` : ""}
              </div>
            )}
          </div>

          {/* Certificate section */}
          <div style={{
            marginTop: 14, padding: "10px 14px", borderRadius: 8,
            background: "#fffbeb", border: "1.5px solid #fde68a",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Award size={18} color="#d97706" />
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#92400e" }}>
                  {cert?.grade ? `Grade: ${cert.grade}` : "Certificate of Achievement"}
                </div>
                <div style={{ fontSize: 11, color: "#b45309" }}>
                  {cert?.marks_obtained != null
                    ? `Score: ${cert.marks_obtained} ${exam.total_marks ? `/ ${exam.total_marks}` : "marks"}`
                    : "Performance Certificate available for this exam"}
                </div>
              </div>
            </div>
            <button
              onClick={() => { onClose(); onOpenCert ? onOpenCert(exam) : nav(`/student-lms/certificates/${cert?.id}`); }}
              style={{
                padding: "6px 14px", borderRadius: 7, border: "none",
                background: "linear-gradient(135deg,#f59e0b,#d97706)",
                color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 5,
                boxShadow: "0 2px 6px rgba(245,158,11,0.25)",
              }}
            >
              <Award size={13} /> View Certificate
            </button>
          </div>

          {exam.instructions && (
            <div style={{ marginTop: 12, padding: "8px 12px", background: "#fffbeb",
              border: "1px solid #fde68a", borderRadius: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}>Instructions: </span>
              <span style={{ fontSize: 12, color: "#78350f" }}>{exam.instructions}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 50 }}>
              <Loader size={28} className="ms2-spin" color="#4f46e5" />
            </div>
          ) : (!hasUtilityPaper && pdfPapers.length === 0 && manualPapers.length === 0) ? (
            <div style={{ textAlign: "center", padding: "50px 20px", color: "#9ca3af" }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, background: "#f3f4f6",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <ClipboardList size={28} color="#d1d5db" />
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>No Question Papers Attached</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Your teacher has not uploaded any question papers for this exam yet.</div>
            </div>
          ) : (
            <>
              {/* Question Paper Generator / Utility Paper */}
              {hasUtilityPaper && (
                <StudentUtilityPaperViewer
                  questionPaperId={exam.question_paper_id}
                  exam={exam}
                  isExpanded={isExpanded}
                />
              )}

              {/* Uploaded PDF Papers */}
              {pdfPapers.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", marginBottom: 10,
                    display: "flex", alignItems: "center", gap: 6 }}>
                    <FileText size={14} color="#4f46e5" /> PDF Question Papers
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {pdfPapers.map((p) => (
                      <div key={p.id} style={{
                        border: "1.5px solid #e0e7ff", borderRadius: 10, padding: "12px 14px",
                        display: "flex", alignItems: "center", gap: 12, background: "#fafbff",
                      }}>
                        <div style={{ width: 38, height: 38, borderRadius: 9, background: "#ede9fe",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <FileText size={18} color="#6d28d9" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.file_name}</div>
                          {p.title && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{p.title}</div>}
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <a href={p.file_url} target="_blank" rel="noreferrer" style={{
                            display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700,
                            color: "#fff", background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                            borderRadius: 7, padding: "5px 12px", textDecoration: "none",
                          }}><Eye size={12} /> View</a>
                          <a href={p.file_url} download target="_blank" rel="noreferrer" style={{
                            display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700,
                            color: "#374151", background: "#f3f4f6", border: "1.5px solid #e5e7eb",
                            borderRadius: 7, padding: "5px 10px", textDecoration: "none",
                          }}>&#11015;</a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Question Papers */}
              {manualPapers.length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", marginBottom: 10,
                    display: "flex", alignItems: "center", gap: 6 }}>
                    <PenLine size={14} color="#7c3aed" /> Manual Question Papers
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {manualPapers.map((p) => (
                      <div key={p.id} style={{
                        border: "1.5px solid #e0e7ff", borderRadius: 10, padding: "12px 14px",
                        display: "flex", alignItems: "center", gap: 12, background: "#fafbff",
                      }}>
                        <div style={{ width: 38, height: 38, borderRadius: 9, background: "#ede9fe",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <PenLine size={17} color="#7c3aed" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                            {p.question_count || 0} questions
                            {p.total_marks ? ` · ${p.total_marks} marks` : ""}
                          </div>
                        </div>
                        <button
                          onClick={() => { onClose(); nav(`/student-lms/manual-papers/${p.id}/build`); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700,
                            color: "#fff", background: "linear-gradient(135deg,#7c3aed,#4f46e5)",
                            border: "none", borderRadius: 7, padding: "6px 14px", cursor: "pointer",
                          }}>
                          <Eye size={12} /> View
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {isOnline ? (
            <div style={{ marginTop: 16, border: "1.5px solid #e0e7ff", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", background: "linear-gradient(90deg,#4f46e5,#7c3aed)",
                display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: "#fff" }}>Online Examination & Results</span>
                <button
                  onClick={() => { onClose(); nav(`/student-lms/exams/${exam.id}/online`); }}
                  style={{
                    padding: "4px 12px", borderRadius: 6, border: "none",
                    background: "#fff", color: "#4f46e5", fontSize: 11, fontWeight: 800, cursor: "pointer",
                  }}
                >
                  Open Exam / Result &rarr;
                </button>
              </div>
              <div style={{ padding: "14px", background: "#fafbff" }}>
                <div style={{ fontSize: 12, color: "#4b5563" }}>
                  View online questions, your submitted answers, scores, and evaluation breakdown.
                </div>
              </div>
            </div>
          ) : (
            <AnswerUploadPanel exam={exam} schoolId={schoolId} studentId={studentId} />
          )}
        </div>
      </div>
    </>
  );
}

/* ── Student Certificate Card ── */
function CertificateCard({ cert }) {
  const nav = useNavigate();
  const issueDate = cert.issued_at
    ? new Date(cert.issued_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "Recently Issued";

  const isOnline = cert.exam_mode === "online" || cert.mode === "online";
  const hasMarks = cert?.marks_obtained != null;
  const score = cert?.marks_obtained;
  const total = cert?.total_marks || 20;
  const percentage = (hasMarks && total) ? Math.round((Number(score) / Number(total)) * 100) : 100;
  const grade = cert?.grade || "Good";

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "16px",
        border: "1px solid #fde68a",
        boxShadow: "0 4px 18px rgba(245,158,11,0.06)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        padding: "20px",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 10px 25px -5px rgba(245,158,11,0.2)";
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 18px rgba(245,158,11,0.06)";
        e.currentTarget.style.transform = "none";
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: "linear-gradient(90deg, #f59e0b, #d97706)",
        }}
      />
      <div>
        {/* Header Row */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", marginBottom: "14px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: "#fef3c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Trophy size={22} color="#d97706" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3
                title={cert.exam_name || "Examination"}
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  lineHeight: "1.3",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {cert.exam_name || "Examination"}
              </h3>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "13px",
                  color: "#64748b",
                  marginTop: "4px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <BookOpen size={14} color="#64748b" style={{ flexShrink: 0 }} />
                <span>{cert.class_name || "Class 1"}{cert.subject_name ? `, ${cert.subject_name}` : ", English"}</span>
              </div>
            </div>
          </div>

          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: "22px", fontWeight: "700", color: "#d97706", lineHeight: 1 }}>
              {cert.total_marks || "20"}
            </div>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "3px" }}>
              marks
            </div>
          </div>
        </div>

        {/* Tags Row: Online/Offline Badge + Published Certificate Tag */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", marginBottom: "14px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "3px 10px",
              borderRadius: "20px",
              background: isOnline ? "#dbeafe" : "#ede9fe",
              color: isOnline ? "#1e40af" : "#6d28d9",
              border: `1px solid ${isOnline ? "#bfdbfe" : "#ddd6fe"}`,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {isOnline ? <Globe size={11} /> : <FileText size={11} />}
            {isOnline ? "Online Exam" : "Offline Exam"}
          </span>

          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "3px 10px",
              borderRadius: "20px",
              background: "#fef3c7",
              color: "#b45309",
              border: "1px solid #fde68a",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Award size={11} />
            Published Certificate
          </span>
        </div>

        {/* Score Block */}
        <div
          style={{
            background: "#f0fdf4",
            borderRadius: "12px",
            padding: "12px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "14px",
            border: "1px solid #bbf7d0",
          }}
        >
          <div
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "50%",
              background: `conic-gradient(#16a34a ${((percentage ?? 100) * 3.6)}deg, #e2e8f0 ${((percentage ?? 100) * 3.6)}deg 360deg)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              padding: "3.5px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "800",
                color: "#15803d",
              }}
            >
              {percentage != null ? `${percentage}%` : "100%"}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
              {Number(score != null ? score : total).toFixed(2)} out of {total} marks
            </div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#15803d", marginTop: "2px" }}>
              Grade: {grade} · Issued on {issueDate}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Button */}
      <div style={{ marginTop: "auto" }}>
        <button
          type="button"
          onClick={() => nav(`/student-lms/certificates/${cert.id}`)}
          style={{
            width: "100%",
            height: "40px",
            borderRadius: "10px",
            border: "1px solid #fde68a",
            background: "#fef3c7",
            color: "#b45309",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            transition: "background 0.15s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#fde68a"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#fef3c7"; }}
        >
          <Award size={15} /> View & Print Certificate
        </button>
      </div>
    </div>
  );
}

/* ── Enhanced Clean Exact Exam Card Component (Online & Offline) ── */
function ExamCard({ exam, onView, cert, onOpenCert, issuingCert }) {
  const nav = useNavigate();
  const isOnline = exam.mode === "online";
  const tagStyle = TAG_STYLES[exam.exam_type] || TAG_STYLES.unit_test;
  const status = isOnline ? getExamStatus(exam) : "offline";

  const { startDateStr, startTimeFormatted, endDateStr, endTimeFormatted } = getExamTimings(exam);

  const isCertPublished = Boolean(cert?.id || cert?.is_published || exam?.is_certificate_published);

  // Calculate percentage if graded or certificate published (only if shared/published for online exams)
  const isResultShared = !isOnline || Number(exam.allow_result_view) === 1;
  const hasMarks = isResultShared && (isCertPublished || cert?.marks_obtained != null || exam?.marks_obtained != null);
  const score = cert?.marks_obtained ?? exam?.marks_obtained;
  const total = cert?.total_marks || exam?.total_marks || 20;
  const percentage = (hasMarks && total && score != null) ? Math.round((Number(score) / Number(total)) * 100) : (isCertPublished ? 100 : null);
  const grade = cert?.grade || exam?.grade || "Good";

  // Render left icon in header box
  const renderIcon = () => {
    if (hasMarks || cert) return <Trophy size={22} color="#3b82f6" />;
    if (exam.exam_type === "mock" || exam.exam_type === "practice") return <Target size={22} color="#3b82f6" />;
    return <FileText size={22} color="#3b82f6" />;
  };

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 18px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        overflow: "hidden",
        padding: "20px",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)";
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 18px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "none";
      }}
    >
      {/* Top Accent Gradient Line */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: "linear-gradient(90deg, #3b82f6 0%, #ec4899 50%, #f59e0b 100%)",
        }}
      />

      {/* Card Content Top */}
      <div>
        {/* Header Row: Icon + (Title & Tag) + Total Marks */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", marginBottom: "14px" }}>
          
          <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flex: 1, minWidth: 0 }}>
            {/* Square Icon Box */}
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: "#eff6ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {renderIcon()}
            </div>

            {/* Title, Category Tag in Same Row, & Subtitle */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <h3
                  title={exam.name}
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#0f172a",
                    margin: 0,
                    lineHeight: "1.3",
                  }}
                >
                  {exam.name}
                </h3>

                {/* Category Pill Tag in the SAME ROW as Exam Name */}
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: tagStyle.bg,
                    color: tagStyle.color,
                    border: `1px solid ${tagStyle.border}`,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Tag size={11} />
                  {tagStyle.label}
                </span>
              </div>

              {/* Subtitle: Class & Subject */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "13px",
                  color: "#64748b",
                  marginTop: "4px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <BookOpen size={14} color="#64748b" style={{ flexShrink: 0 }} />
                <span>{exam.class_name || "Class 1"}{exam.subject_name ? `, ${exam.subject_name}` : ", English"}</span>
              </div>
            </div>
          </div>

          {/* Right Marks Column */}
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: "22px", fontWeight: "700", color: "#3b82f6", lineHeight: 1 }}>
              {exam.total_marks || "-"}
            </div>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "3px" }}>
              marks
            </div>
          </div>
        </div>

        {/* Info Box: Start Date & Time, End Date & Time, Duration */}
        <div
          style={{
            background: "#f8fafc",
            borderRadius: "12px",
            padding: "12px 16px",
            marginBottom: "14px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            border: "1px solid #f1f5f9",
          }}
        >
          {/* Row 1: Start Date & Start Time */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#334155" }}>
              <Calendar size={14} color="#64748b" />
              <span><strong style={{ fontWeight: 600, color: "#475569" }}>Start:</strong> {startDateStr}</span>
            </div>
            {startTimeFormatted && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#334155" }}>
                <Clock size={14} color="#64748b" />
                <span>{startTimeFormatted}</span>
              </div>
            )}
          </div>

          {/* Row 2: End Date & End Time */}
          {endTimeFormatted && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#334155" }}>
                <Calendar size={14} color="#64748b" />
                <span><strong style={{ fontWeight: 600, color: "#475569" }}>End:</strong> {endDateStr}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#334155" }}>
                <Clock size={14} color="#64748b" />
                <span>{endTimeFormatted}</span>
              </div>
            </div>
          )}

          {/* Row 3: Duration */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#334155" }}>
            <Clock size={14} color="#64748b" />
            <span>{exam.duration_minutes ? `${exam.duration_minutes} minutes duration` : "50 minutes duration"}</span>
          </div>
        </div>

        {/* Status / Score Block */}
        {hasMarks ? (
          /* Scored / Result Available Box (Clean Green with conic-gradient % badge) */
          <div
            style={{
              background: "#f0fdf4",
              borderRadius: "12px",
              padding: "12px 16px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "14px",
              border: "1px solid #bbf7d0",
            }}
          >
            {/* Conic Gradient Progress Circle (75% fills 75%, 100% fills full circle) */}
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                background: `conic-gradient(#16a34a ${((percentage ?? 100) * 3.6)}deg, #e2e8f0 ${((percentage ?? 100) * 3.6)}deg 360deg)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                padding: "3.5px",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  background: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: "800",
                  color: "#15803d",
                }}
              >
                {percentage != null ? `${percentage}%` : "100%"}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                  {Number(score || 0).toFixed(2)} out of {total || 20} marks
                </div>
                {isCertPublished && (
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      background: "#fef3c7",
                      color: "#b45309",
                      border: "1px solid #fde68a",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Award size={11} /> Published
                  </span>
                )}
              </div>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#15803d", marginTop: "2px" }}>
                Grade: {grade}
              </div>
            </div>
          </div>
        ) : status === "open" ? (
          /* Live Exam Ready Box */
          <div
            style={{
              background: "#ecfdf5",
              borderRadius: "12px",
              padding: "12px 16px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              border: "1px solid #a7f3d0",
            }}
          >
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "#d1fae5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Play size={16} color="#059669" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#065f46" }}>
                Live Exam Ready
              </div>
              <div style={{ fontSize: "12px", color: "#047857", marginTop: "2px" }}>
                Exam is live now. Click below to start.
              </div>
            </div>
          </div>
        ) : status === "not_started" ? (
          /* Upcoming Box */
          <div
            style={{
              background: "#eff6ff",
              borderRadius: "12px",
              padding: "12px 16px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              border: "1px solid #bfdbfe",
            }}
          >
            <Clock size={18} color="#2563eb" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#1e40af" }}>
                Exam Scheduled
              </div>
              <div style={{ fontSize: "12px", color: "#3b82f6", marginTop: "2px" }}>
                Starts at {startTimeFormatted || exam.start_time || "Scheduled Time"}
              </div>
            </div>
          </div>
        ) : (
          /* Result pending / Exam ended Box */
          <div
            style={{
              background: "#f8fafc",
              borderRadius: "12px",
              padding: "12px 16px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Clock size={18} color="#64748b" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                Result pending
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                Exam ended. Awaiting publication.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Buttons Row */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "auto" }}>
        {isOnline ? (
          <>
            {hasMarks ? (
              /* Scored / Result ready */
              <>
                <button
                  type="button"
                  onClick={() => nav(`/student-lms/exams/${exam.id}/online`)}
                  style={{
                    flex: isCertPublished ? 1.1 : 1.5,
                    height: "40px",
                    borderRadius: "10px",
                    border: "none",
                    background: "#4338ca",
                    color: "#ffffff",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    transition: "background 0.15s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#3730a3"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#4338ca"; }}
                >
                  <BarChart2 size={15} /> View result
                </button>

                <button
                  type="button"
                  onClick={() => onView(exam)}
                  style={{
                    height: "40px",
                    padding: "0 16px",
                    borderRadius: "10px",
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#0f172a",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#ffffff"; }}
                >
                  <Eye size={15} /> Paper
                </button>

                {isCertPublished && (
                  <button
                    type="button"
                    onClick={() => onOpenCert(exam)}
                    disabled={issuingCert === exam.id}
                    style={{
                      height: "40px",
                      padding: "0 16px",
                      borderRadius: "10px",
                      border: "1px solid #fde68a",
                      background: "#fef3c7",
                      color: "#b45309",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: issuingCert === exam.id ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#fde68a"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fef3c7"; }}
                  >
                    {issuingCert === exam.id ? <Loader size={14} className="ms2-spin" /> : <Award size={15} />}
                    Certificate
                  </button>
                )}
              </>
            ) : status === "open" ? (
              /* Live Exam */
              <>
                <button
                  type="button"
                  onClick={() => nav(`/student-lms/exams/${exam.id}/online`)}
                  style={{
                    flex: 1,
                    height: "40px",
                    borderRadius: "10px",
                    border: "none",
                    background: "#16a34a",
                    color: "#ffffff",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#15803d"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#16a34a"; }}
                >
                  <Play size={15} /> Start Exam
                </button>

                <button
                  type="button"
                  onClick={() => onView(exam)}
                  style={{
                    height: "40px",
                    padding: "0 20px",
                    borderRadius: "10px",
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#0f172a",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <Eye size={15} /> Paper
                </button>
              </>
            ) : (
              /* Result Pending / Scheduled */
              <>
                <button
                  type="button"
                  disabled
                  style={{
                    flex: 1,
                    height: "40px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    color: "#94a3b8",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <BarChart2 size={15} /> View result
                </button>

                <button
                  type="button"
                  onClick={() => onView(exam)}
                  style={{
                    height: "40px",
                    padding: "0 20px",
                    borderRadius: "10px",
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#0f172a",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#ffffff"; }}
                >
                  <Eye size={15} /> Paper
                </button>
              </>
            )}
          </>
        ) : (
          /* Offline Exam */
          <>
            <button
              type="button"
              onClick={() => onView(exam)}
              style={{
                flex: 1,
                height: "40px",
                borderRadius: "10px",
                border: "none",
                background: "#4338ca",
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#3730a3"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#4338ca"; }}
            >
              <FileText size={15} /> Question Paper
            </button>

            <button
              type="button"
              onClick={() => onView(exam)}
              style={{
                flex: 1,
                height: "40px",
                borderRadius: "10px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#0f172a",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#ffffff"; }}
            >
              <Upload size={15} /> Answer Sheet
            </button>

            {isCertPublished && (
              <button
                type="button"
                onClick={() => onOpenCert(exam)}
                style={{
                  height: "40px",
                  padding: "0 16px",
                  borderRadius: "10px",
                  border: "1px solid #fde68a",
                  background: "#fef3c7",
                  color: "#b45309",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#fde68a"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fef3c7"; }}
              >
                <Award size={15} /> Certificate
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function StudentExams() {
  const nav      = useNavigate();
  const userData = getStudentData();
  const schoolId       = userData?.school_id || userData?.admin_id || null;
  const studentId      = userData?.student_id || userData?.id || userData?.admin_id || null;
  const studentClassId = userData?.class_id || null;

  const [exams,        setExams]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [filter,       setFilter]       = useState("all");
  const [certFilter,   setCertFilter]   = useState("all"); // 'all' | 'online' | 'offline'
  const [viewExam,     setViewExam]     = useState(null);
  const [myCerts,      setMyCerts]      = useState({}); // { [exam_id]: cert }
  const [rawCertsList, setRawCertsList] = useState([]);
  
  // DEFAULT ACTIVE TAB IS "online" as requested!
  const [mainTab,     setMainTab]     = useState("online"); // 'online' | 'offline' | 'all' | 'certs'
  const [issuingCert, setIssuingCert] = useState(null);

  const loadExams = () => {
    if (!schoolId) return;
    setLoading(true);
    if (studentId) {
      lmsGetCertificates({ student_id: studentId, school_id: schoolId })
        .then((res) => {
          const raw = res?.data?.data || [];
          setRawCertsList(raw);
          const map = {};
          raw.forEach((c) => {
            if (c.exam_id != null) {
              map[c.exam_id] = c;
              map[String(c.exam_id)] = c;
              if (Number(c.exam_id)) map[Number(c.exam_id)] = c;
            }
          });
          setMyCerts(map);
        })
        .catch(() => {});
    }
    lmsGetExams({ school_id: schoolId })
      .then((res) => {
        const all = res?.data?.data || [];
        setExams(studentClassId
          ? all.filter((e) => String(e.class_id) === String(studentClassId))
          : all
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadExams(); }, [schoolId]);

  const handleOpenCert = (exam) => {
    const existing = myCerts[exam.id] || myCerts[String(exam.id)] || (exam.id ? myCerts[Number(exam.id)] : null);
    if (existing?.id) {
      nav(`/student-lms/certificates/${existing.id}`);
      return;
    }
    toast.info("Certificate for this examination has not been published yet.");
  };

  // Group exams by mode
  const onlineExams  = exams.filter((e) => e.mode === "online");
  const offlineExams = exams.filter((e) => e.mode !== "online");

  // Determine current active pool of exams based on tab
  const currentPool = mainTab === "online"
    ? onlineExams
    : mainTab === "offline"
    ? offlineExams
    : exams;

  // Stats by type for the current active pool
  const typeCounts = currentPool.reduce((acc, e) => {
    acc[e.exam_type] = (acc[e.exam_type] || 0) + 1;
    return acc;
  }, {});

  const filtered = currentPool.filter((e) => {
    const matchSearch = !search || e.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || e.exam_type === filter;
    return matchSearch && matchFilter;
  });

  const examMap = {};
  exams.forEach((e) => {
    if (e.id != null) {
      examMap[e.id] = e;
      examMap[String(e.id)] = e;
      if (Number(e.id)) examMap[Number(e.id)] = e;
    }
  });

  const enrichedCerts = rawCertsList.map((c) => {
    const matchedExam = (c.exam_id != null && (examMap[c.exam_id] || examMap[String(c.exam_id)] || examMap[Number(c.exam_id)])) || null;
    const mode = c.exam_mode || matchedExam?.mode || c.mode || (c.is_online ? "online" : "offline");
    return {
      ...c,
      mode: mode,
      exam_mode: mode,
      exam_name: c.exam_name || matchedExam?.name || "Examination",
      class_name: c.class_name || matchedExam?.class_name,
      subject_name: c.subject_name || matchedExam?.subject_name,
      total_marks: c.total_marks || matchedExam?.total_marks || 20,
    };
  });

  const onlineCertsCount  = enrichedCerts.filter((c) => c.exam_mode === "online").length;
  const offlineCertsCount = enrichedCerts.filter((c) => c.exam_mode !== "online").length;

  const filteredCerts = enrichedCerts.filter((c) => {
    const matchMode =
      certFilter === "all"
        ? true
        : certFilter === "online"
        ? c.exam_mode === "online"
        : c.exam_mode !== "online";
    if (!matchMode) return false;
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (c.exam_name && c.exam_name.toLowerCase().includes(term)) ||
      (c.subject_name && c.subject_name.toLowerCase().includes(term)) ||
      (c.grade && c.grade.toLowerCase().includes(term))
    );
  });

  const statItems = Object.entries(EXAM_TYPE_LABELS).filter(([k]) => typeCounts[k]);

  return (
    <div className="lms-content-inner">
      {/* Top Banner Header */}
      <div style={{
        background: "#fff", borderRadius: 14, marginBottom: 14,
        border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden",
      }}>
        <div style={{ height: 3, background: "linear-gradient(90deg,#3b82f6,#8b5cf6,#f59e0b)" }} />
        <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            boxShadow: "0 3px 8px rgba(79,70,229,0.25)",
          }}>
            <ClipboardList size={20} color="#fff" />
          </div>
          <div style={{ flex: "1 1 250px" }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#0f172a" }}>My Exams & Certificates</div>
            <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 1 }}>
              Take online tests, view offline classroom papers, submit answer sheets, and view certificates
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
            <div style={{
              textAlign: "center",
              background: "#eff6ff", borderRadius: 10, padding: "5px 14px", border: "1.5px solid #bfdbfe",
            }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#1d4ed8", lineHeight: 1.1 }}>{onlineExams.length}</div>
              <div style={{ fontSize: 9.5, color: "#2563eb", fontWeight: 800, letterSpacing: "0.05em", marginTop: 2 }}>ONLINE</div>
            </div>
            <div style={{
              textAlign: "center",
              background: "#faf5ff", borderRadius: 10, padding: "5px 14px", border: "1.5px solid #e9d5ff",
            }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#7e22ce", lineHeight: 1.1 }}>{offlineExams.length}</div>
              <div style={{ fontSize: 9.5, color: "#9333ea", fontWeight: 800, letterSpacing: "0.05em", marginTop: 2 }}>OFFLINE</div>
            </div>
            <div style={{
              textAlign: "center",
              background: "#fffbeb", borderRadius: 10, padding: "5px 14px", border: "1.5px solid #fde68a",
            }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#d97706", lineHeight: 1.1 }}>{enrichedCerts.length}</div>
              <div style={{ fontSize: 9.5, color: "#b45309", fontWeight: 800, letterSpacing: "0.05em", marginTop: 2 }}>CERTIFICATES</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Mode Navigation Tabs (Online Selected by Default) */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          borderBottom: "2px solid #e2e8f0",
          marginBottom: "14px",
          overflowX: "auto",
          scrollbarWidth: "none",
        }}
      >
        {/* Tab 1: Online Exams (Default) */}
        <button
          type="button"
          onClick={() => { setMainTab("online"); setFilter("all"); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "online" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "online" ? "#1c498d" : "#64748b",
            borderBottom: mainTab === "online" ? "3px solid #1c498d" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <Globe size={16} color={mainTab === "online" ? "#1c498d" : "#94a3b8"} />
          <span>Online Exams</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "online" ? "#dbeafe" : "#f1f5f9",
              color: mainTab === "online" ? "#1e40af" : "#64748b",
            }}
          >
            {onlineExams.length}
          </span>
        </button>

        {/* Tab 2: Offline Exams */}
        <button
          type="button"
          onClick={() => { setMainTab("offline"); setFilter("all"); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "offline" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "offline" ? "#1c498d" : "#64748b",
            borderBottom: mainTab === "offline" ? "3px solid #1c498d" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <FileText size={16} color={mainTab === "offline" ? "#1c498d" : "#94a3b8"} />
          <span>Offline Exams</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "offline" ? "#dbeafe" : "#f1f5f9",
              color: mainTab === "offline" ? "#1e40af" : "#64748b",
            }}
          >
            {offlineExams.length}
          </span>
        </button>

        {/* Tab 3: All Exams */}
        <button
          type="button"
          onClick={() => { setMainTab("all"); setFilter("all"); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "all" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "all" ? "#1c498d" : "#64748b",
            borderBottom: mainTab === "all" ? "3px solid #1c498d" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <Layers size={16} color={mainTab === "all" ? "#1c498d" : "#94a3b8"} />
          <span>All Exams</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "all" ? "#dbeafe" : "#f1f5f9",
              color: mainTab === "all" ? "#1e40af" : "#64748b",
            }}
          >
            {exams.length}
          </span>
        </button>

        {/* Tab 4: My Certificates */}
        <button
          type="button"
          onClick={() => setMainTab("certs")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "certs" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "certs" ? "#d97706" : "#64748b",
            borderBottom: mainTab === "certs" ? "3px solid #d97706" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <Award size={16} color={mainTab === "certs" ? "#d97706" : "#94a3b8"} />
          <span>My Certificates</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "certs" ? "#fef3c7" : "#f1f5f9",
              color: mainTab === "certs" ? "#b45309" : "#64748b",
            }}
          >
            {enrichedCerts.length}
          </span>
        </button>
      </div>

      {/* Search + Filter Bar */}
      <div className="crs-filters" style={{ marginBottom: 12 }}>
        <div className="crs-search-wrap" style={{ flex: 1, padding: "7px 12px" }}>
          <Search size={14} />
          <input
            placeholder={
              mainTab === "online"
                ? "Search online exams by name or subject..."
                : mainTab === "offline"
                ? "Search offline exams by name or subject..."
                : mainTab === "certs"
                ? "Search certificates by exam, subject, or grade..."
                : "Search all exams..."
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button onClick={loadExams} style={{
          padding: "7px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb",
          background: "#fff", color: "#6b7280", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600,
        }}><RefreshCw size={13} /> Refresh</button>
      </div>

      {/* EXAMS GRIDS (ONLINE, OFFLINE, ALL) */}
      {mainTab !== "certs" && (
        <>
          {/* Category Filter Chips */}
          {statItems.length > 0 && (
            <div style={{ display: "flex", gap: "7px", marginBottom: "14px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setFilter("all")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 12px",
                  borderRadius: "18px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  border: filter === "all" ? "1.5px solid #1c498d" : "1px solid #e2e8f0",
                  background: filter === "all" ? "#eff6ff" : "#ffffff",
                  color: filter === "all" ? "#1c498d" : "#64748b",
                  transition: "all 0.15s ease",
                }}
              >
                <BarChart2 size={12} /> All {mainTab === "online" ? "Online" : mainTab === "offline" ? "Offline" : ""} ({currentPool.length})
              </button>
              {statItems.map(([key, label]) => {
                const tc = EXAM_TYPE_COLORS[key];
                const active = filter === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(active ? "all" : key)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "4px 12px",
                      borderRadius: "18px",
                      fontSize: "12px",
                      fontWeight: "700",
                      cursor: "pointer",
                      border: active ? `1.5px solid ${tc.color}` : "1px solid #e2e8f0",
                      background: active ? tc.bg : "#ffffff",
                      color: active ? tc.color : "#64748b",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {label} ({typeCounts[key]})
                  </button>
                );
              })}
            </div>
          )}

          {/* Grid / State views */}
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "70px 0" }}>
              <Loader size={30} className="ms2-spin" color="#1c498d" />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "70px 20px", background: "#fff",
              borderRadius: 16, border: "1.5px solid #e2e8f0" }}>
              <div style={{ width: 70, height: 70, borderRadius: 18, background: "#f1f5f9",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
                {mainTab === "online" ? <Globe size={32} color="#94a3b8" /> : <ClipboardList size={32} color="#94a3b8" />}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#334155", marginBottom: 6 }}>
                {search || filter !== "all"
                  ? "No exams match your search criteria"
                  : mainTab === "online"
                  ? "No Online Exams Scheduled"
                  : mainTab === "offline"
                  ? "No Offline Exams Scheduled"
                  : "No exams scheduled yet"}
              </div>
              <div style={{ fontSize: 13, color: "#94a3b8", maxWidth: 360, margin: "0 auto" }}>
                {search || filter !== "all"
                  ? "Try clearing your search query or selecting another filter category."
                  : "Check back later when your teachers post new examinations."}
              </div>
              {(search || filter !== "all") && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); setFilter("all"); }}
                  style={{
                    marginTop: 16, padding: "8px 20px", borderRadius: 8, border: "none",
                    background: "#1c498d", color: "#fff",
                    fontSize: 13, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                gap: "20px",
              }}
            >
              {filtered.map((exam) => (
                <ExamCard
                  key={exam.id}
                  exam={exam}
                  onView={setViewExam}
                  cert={myCerts[exam.id] || myCerts[String(exam.id)] || (exam.id ? myCerts[Number(exam.id)] : null)}
                  onOpenCert={handleOpenCert}
                  issuingCert={issuingCert}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* TAB 4: MY CERTIFICATES */}
      {mainTab === "certs" && (
        <>
          {/* Certificate Mode Filter Chips (All, Online, Offline) */}
          {enrichedCerts.length > 0 && (
            <div style={{ display: "flex", gap: "7px", marginBottom: "14px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setCertFilter("all")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 12px",
                  borderRadius: "18px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  border: certFilter === "all" ? "1.5px solid #d97706" : "1px solid #e2e8f0",
                  background: certFilter === "all" ? "#fffbeb" : "#ffffff",
                  color: certFilter === "all" ? "#d97706" : "#64748b",
                  transition: "all 0.15s ease",
                }}
              >
                <Award size={12} /> All Certificates ({enrichedCerts.length})
              </button>
              <button
                type="button"
                onClick={() => setCertFilter("online")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 12px",
                  borderRadius: "18px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  border: certFilter === "online" ? "1.5px solid #2563eb" : "1px solid #e2e8f0",
                  background: certFilter === "online" ? "#eff6ff" : "#ffffff",
                  color: certFilter === "online" ? "#1d4ed8" : "#64748b",
                  transition: "all 0.15s ease",
                }}
              >
                <Globe size={12} /> Online Exams ({onlineCertsCount})
              </button>
              <button
                type="button"
                onClick={() => setCertFilter("offline")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 12px",
                  borderRadius: "18px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  border: certFilter === "offline" ? "1.5px solid #7c3aed" : "1px solid #e2e8f0",
                  background: certFilter === "offline" ? "#faf5ff" : "#ffffff",
                  color: certFilter === "offline" ? "#7e22ce" : "#64748b",
                  transition: "all 0.15s ease",
                }}
              >
                <FileText size={12} /> Offline Exams ({offlineCertsCount})
              </button>
            </div>
          )}

          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "70px 0" }}>
              <Loader size={30} className="ms2-spin" color="#f59e0b" />
            </div>
          ) : filteredCerts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "70px 20px", background: "#fff",
              borderRadius: 16, border: "1.5px solid #e2e8f0" }}>
              <div style={{ width: 70, height: 70, borderRadius: 18, background: "#fffbeb",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px",
                border: "1.5px solid #fde68a" }}>
                <Award size={34} color="#d97706" />
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#334155", marginBottom: 6 }}>
                {search ? "No certificates match your search" : "No Certificates Published Yet"}
              </div>
              <div style={{ fontSize: 13, color: "#94a3b8", maxWidth: 420, margin: "0 auto" }}>
                {search
                  ? "Try searching by another exam name or grade."
                  : "When your teachers or administrators evaluate your exams and publish certificates, they will appear here."}
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                gap: "20px",
              }}
            >
              {filteredCerts.map((cert) => (
                <CertificateCard key={cert.id} cert={cert} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Detail Panel */}
      {viewExam && (
        <ExamDetailPanel
          exam={viewExam}
          schoolId={schoolId}
          studentId={studentId}
          nav={nav}
          cert={myCerts[viewExam.id]}
          onOpenCert={handleOpenCert}
          onClose={() => setViewExam(null)}
        />
      )}
    </div>
  );
}
